import { useMemo } from "react";
import * as THREE from "three";
import { Cyl } from "./Box";

const bend = (geo, k, axis = "x") => {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    pos.setZ(i, -k * (axis === "x" ? x * x : y * y) - 0.15 * k * y * y);
  }
  geo.computeVertexNormals();
  return geo;
};

const monsteraLeaf = () => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.bezierCurveTo(0.22, 0.02, 0.32, 0.25, 0.26, 0.45);
  s.bezierCurveTo(0.2, 0.62, 0.06, 0.7, 0, 0.72);
  s.bezierCurveTo(-0.06, 0.7, -0.2, 0.62, -0.26, 0.45);
  s.bezierCurveTo(-0.32, 0.25, -0.22, 0.02, 0, 0);
  [0.2, 0.36, 0.52].forEach((y, i) => {
    [-1, 1].forEach((side) => {
      const h = new THREE.Path();
      h.absellipse(side * (0.13 + i * 0.01), y, 0.035, 0.018, 0, Math.PI * 2, false, side * 0.5);
      s.holes.push(h);
    });
  });
  return bend(new THREE.ShapeGeometry(s, 14), 0.6);
};

const bladeGeo = () => {
  const s = new THREE.Shape();
  s.moveTo(-0.045, 0);
  s.quadraticCurveTo(-0.06, 0.6, 0, 1.1);
  s.quadraticCurveTo(0.06, 0.6, 0.045, 0);
  s.lineTo(-0.045, 0);
  return bend(new THREE.ShapeGeometry(s, 12), 2.2);
};

const heartLeaf = () => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.bezierCurveTo(0.06, 0.02, 0.07, 0.07, 0, 0.12);
  s.bezierCurveTo(-0.07, 0.07, -0.06, 0.02, 0, 0);
  return bend(new THREE.ShapeGeometry(s, 8), 3);
};

const Leaf = ({ geo, position, rotation, scale, color }) => (
  <mesh geometry={geo} position={position} rotation={rotation} scale={scale} castShadow receiveShadow>
    <meshStandardMaterial color={color} roughness={0.55} side={THREE.DoubleSide} />
  </mesh>
);

const Stem = ({ from, to, color = "#4E6B3A", r = 0.012 }) => {
  const geo = useMemo(() => {
    const mid = new THREE.Vector3((from[0] + to[0]) / 2, Math.max(from[1], to[1]) + 0.05, (from[2] + to[2]) / 2);
    const curve = new THREE.QuadraticBezierCurve3(new THREE.Vector3(...from), mid, new THREE.Vector3(...to));
    return new THREE.TubeGeometry(curve, 10, r, 6, false);
  }, [from, to, r]);
  return (
    <mesh geometry={geo} castShadow>
      <meshStandardMaterial color={color} roughness={0.7} />
    </mesh>
  );
};

const Pot = ({ r = 0.3, h = 0.55, c = "#C85A32" }) => (
  <group>
    <Cyl p={[0, h / 2, 0]} a={[r, r * 0.78, h, 32]} c={c} r={0.8} t="plaster" tr={[2, 1]} />
    <Cyl p={[0, h - 0.02, 0]} a={[r + 0.02, r + 0.02, 0.05, 32]} c={c} r={0.8} t="plaster" tr={[3, 1]} />
    <Cyl p={[0, h - 0.04, 0]} a={[r - 0.02, r - 0.02, 0.02, 28]} c="#2E1C10" r={1} t="rug" />
  </group>
);

export const Monstera = ({ position, scale = 1 }) => {
  const geo = useMemo(monsteraLeaf, []);
  const leaves = useMemo(() => {
    const out = [];
    for (let i = 0; i < 11; i += 1) {
      const a = i * 2.4;
      const h = 0.75 + (i % 4) * 0.22;
      const d = 0.18 + (i % 3) * 0.1;
      out.push({ a, tip: [Math.cos(a) * d, h, Math.sin(a) * d], s: 0.75 + (i % 3) * 0.18, tilt: -0.5 - (i % 3) * 0.25, c: ["#3F6B3A", "#4E7A42", "#365E33"][i % 3] });
    }
    return out;
  }, []);
  return (
    <group position={position} scale={scale}>
      <Pot />
      {leaves.map((l, i) => (
        <group key={i}>
          <Stem from={[0, 0.5, 0]} to={l.tip} />
          <Leaf geo={geo} position={l.tip} rotation={[l.tilt, -l.a + Math.PI / 2, 0]} scale={l.s} color={l.c} />
        </group>
      ))}
    </group>
  );
};

export const SnakePlant = ({ position, scale = 1 }) => {
  const geo = useMemo(bladeGeo, []);
  return (
    <group position={position} scale={scale}>
      <Pot r={0.26} h={0.5} c="#CDBEA6" />
      {Array.from({ length: 13 }, (_, i) => {
        const a = i * 2.39;
        const d = 0.04 + (i % 4) * 0.035;
        return (
          <Leaf key={i} geo={geo} position={[Math.cos(a) * d, 0.48, Math.sin(a) * d]}
            rotation={[Math.cos(a) * 0.18, a, Math.sin(a) * 0.18]} scale={[1, 0.8 + (i % 5) * 0.12, 1]}
            color={i % 2 ? "#3D5E3A" : "#56743F"} />
        );
      })}
    </group>
  );
};

export const Pothos = ({ position, length = 1.1 }) => {
  const geo = useMemo(heartLeaf, []);
  const vines = [[0.25, 0.1], [-0.2, 0.15], [0.05, -0.2]];
  return (
    <group position={position}>
      <Pot r={0.11} h={0.16} c="#EFE3CF" />
      {vines.map(([vx, vz], v) => (
        <group key={v}>
          {Array.from({ length: 9 }, (_, i) => {
            const t = i / 8;
            const p = [vx * (0.5 + t), 0.16 - t * length * (0.6 + v * 0.2) + Math.sin(t * 3) * 0.04, vz * (0.6 + t) + 0.12 * t];
            return <Leaf key={i} geo={geo} position={p} rotation={[0.8 + (i % 2) * 0.3, i * 1.3, 0.3]} scale={1.1 - t * 0.3} color={i % 2 ? "#5E8A3E" : "#7FA352"} />;
          })}
        </group>
      ))}
    </group>
  );
};

export const Succulent = ({ position }) => (
  <group position={position}>
    <Pot r={0.09} h={0.14} c="#EFE3CF" />
    {Array.from({ length: 14 }, (_, i) => {
      const a = i * 2.4;
      const ring = i < 6 ? 0.03 : 0.065;
      return (
        <mesh key={i} position={[Math.cos(a) * ring, 0.17, Math.sin(a) * ring]} rotation={[Math.sin(a) * (i < 6 ? 0.5 : 1.1), 0, -Math.cos(a) * (i < 6 ? 0.5 : 1.1)]} scale={[0.5, 1, 0.3]} castShadow>
          <coneGeometry args={[0.05, 0.12, 8]} />
          <meshStandardMaterial color={i < 6 ? "#9DBB8E" : "#7FA07A"} roughness={0.5} />
        </mesh>
      );
    })}
  </group>
);

// Petit érable en pot — clin d'œil aux jeunes pousses de Hirayama (Perfect Days)
export const Sapling = ({ position }) => {
  const geo = useMemo(heartLeaf, []);
  return (
    <group position={position}>
      <Pot r={0.06} h={0.1} c="#3A3A3A" />
      <Stem from={[0, 0.09, 0]} to={[0.01, 0.3, 0]} color="#6B4A2A" r={0.005} />
      {[[0.03, 0.28, 0], [-0.03, 0.25, 0.02], [0, 0.31, -0.03], [0.02, 0.22, 0.03]].map((p, i) => (
        <Leaf key={i} geo={geo} position={p} rotation={[0.6, i * 1.6, 0]} scale={0.6} color={i % 2 ? "#7FAE4E" : "#A9C25A"} />
      ))}
    </group>
  );
};
